/**
 * EventsManagerPanel — Events / flyer projects.
 * Placeholder: Convex-backed list coming in Phase 1 canvas integration.
 */
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { LayoutGrid, Plus, ExternalLink, Pencil, Trash2, Copy } from 'lucide-react';

export function EventsManagerPanel() {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const teamId = useAppStore((s) => s.currentTeamId) ?? undefined;
  const projects = useQuery(api.projects.list, {
    accountId,
    teamId: teamId as import('../../convex/_generated/dataModel').Id<'teams'> | undefined,
  });
  const createProject = useMutation(api.projects.create);
  const updateProject = useMutation(api.projects.update);
  const removeProject = useMutation(api.projects.remove);
  const duplicateProject = useMutation(api.projects.duplicate);
  const { currentProjectId, setCurrentProjectId, setActivePanel } = useAppStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleNewProject = async () => {
    try {
      const p = await createProject({
      accountId,
      teamId: teamId as import('../../convex/_generated/dataModel').Id<'teams'> | undefined,
      name: 'Untitled Flyer',
    });
      if (p?.id) {
        setCurrentProjectId(p.id);
        setActivePanel('canvas');
      }
    } catch (err) {
      console.error('Create project failed:', err);
    }
  };

  const handleOpen = (id: string) => {
    setCurrentProjectId(id);
    setActivePanel('canvas');
  };

  const handleStartRename = (id: string, name: string) => {
    setEditingId(id);
    setEditName(name);
  };

  const handleSaveRename = async () => {
    if (!editingId || !editName.trim()) {
      setEditingId(null);
      return;
    }
    try {
      await updateProject({ accountId, id: editingId, name: editName.trim() });
      setEditingId(null);
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this project? This cannot be undone.')) return;
    try {
      await removeProject({ accountId, id });
      if (currentProjectId === id) setCurrentProjectId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleDuplicate = async (id: string, name: string) => {
    try {
      const p = await duplicateProject({ accountId, id, new_name: `${name} (copy)` });
      if (p?.id) {
        setCurrentProjectId(p.id);
        setActivePanel('canvas');
      }
    } catch (err) {
      console.error('Duplicate failed:', err);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <div className="flex-shrink-0 border-b border-border p-4 bg-card/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-foreground">Events & Projects</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {projects ? `${projects.length} project${projects.length !== 1 ? 's' : ''}` : 'Loading...'}
            </p>
          </div>
          <button
            onClick={handleNewProject}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-all active:scale-[0.98] hover:shadow-sm hover:shadow-primary/20"
          >
            <Plus className="h-4 w-4" />
            New
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {projects === undefined ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center animate-fade-in">
            <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center">
              <LayoutGrid className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <div>
              <p className="font-medium text-foreground/80">No projects yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first event flyer
              </p>
            </div>
            <button
              onClick={handleNewProject}
              className="mt-2 flex items-center gap-1.5 rounded-lg bg-primary/10 text-primary px-4 py-2 text-sm font-medium hover:bg-primary/20 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {projects.map((p) => (
              <div
                key={p._id}
                className={`group rounded-xl border bg-card p-3 transition-all duration-150 cursor-pointer hover:shadow-sm ${
                  currentProjectId === p.id
                    ? 'border-primary/40 shadow-sm shadow-primary/5'
                    : 'border-border hover:border-primary/20'
                }`}
                onClick={() => editingId !== p.id && handleOpen(p.id)}
              >
                {editingId === p.id ? (
                  <div className="flex gap-2 animate-fade-in" onClick={(e) => e.stopPropagation()}>
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                      className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
                      autoFocus
                    />
                    <button onClick={handleSaveRename} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors">Save</button>
                    <button onClick={() => setEditingId(null)} className="rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-muted transition-colors">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        currentProjectId === p.id ? 'bg-primary/15' : 'bg-muted/50'
                      }`}>
                        <LayoutGrid className={`h-4 w-4 ${currentProjectId === p.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate text-sm">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Updated {new Date(p.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleDuplicate(p.id, p.name)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Duplicate">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleStartRename(p.id, p.name)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Rename">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
